import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";
import { TextArea } from 'devextreme-react/text-area';
import { List } from 'devextreme-react/list';
import PrimaryTab from "./PrimaryTab";
import ExpressionBaseQueryAvailableFields from "../../../../common/ExpressionBaseQueryAvailableFields";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});
    const initialFormFields = {
        ROWID: '00000000-0000-0000-0000-000000000000',
        RECORD: '',
        EXPRESSIONCODE: '',
        DESCRIPTION: '',
        STATUS: 'A',
        COMMENT: '',
        SQLEXPRESSION: ''
    };
    const [formFields, setformFields] = useState(initialFormFields);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const accessToken = props?.oidc?.user?.access_token;


    // Hardcoded available functions
    const availableFunctions = [
        // Date Functions
        { category: 'Date Functions', name: 'DATEADD' },
        { category: 'Date Functions', name: 'DATEDIFF' },
        { category: 'Date Functions', name: 'DATENAME' },
        { category: 'Date Functions', name: 'DATEPART' },
        { category: 'Date Functions', name: 'DAY' },
        { category: 'Date Functions', name: 'GETDATE' },
        { category: 'Date Functions', name: 'GETUTCDATE' },
        { category: 'Date Functions', name: 'MONTH' },
        { category: 'Date Functions', name: 'YEAR' },
        // String Functions
        { category: 'String Functions', name: 'ASCII' },
        { category: 'String Functions', name: 'CHAR' },
        { category: 'String Functions', name: 'CHARINDEX' },
        { category: 'String Functions', name: 'DIFFERENCE' },
        { category: 'String Functions', name: 'LEFT' },
        { category: 'String Functions', name: 'LEN' },
        { category: 'String Functions', name: 'LOWER' },
        { category: 'String Functions', name: 'LTRIM' },
        { category: 'String Functions', name: 'NCHAR' },
        { category: 'String Functions', name: 'PATINDEX' },
        { category: 'String Functions', name: 'QUOTENAME' },
        { category: 'String Functions', name: 'REPLACE' },
        { category: 'String Functions', name: 'REPLICATE' },
        { category: 'String Functions', name: 'REVERSE' },
        { category: 'String Functions', name: 'RIGHT' },
        { category: 'String Functions', name: 'RTRIM' },
        { category: 'String Functions', name: 'SOUNDEX' },
        { category: 'String Functions', name: 'SPACE' },
        { category: 'String Functions', name: 'STR' },
        { category: 'String Functions', name: 'STUFF' },
        { category: 'String Functions', name: 'SUBSTRING' },
        { category: 'String Functions', name: 'UNICODE' },
        { category: 'String Functions', name: 'UPPER' },
        // Aggregate Functions
        { category: 'Aggregate Functions', name: 'AVG' },
        { category: 'Aggregate Functions', name: 'COUNT' },
        { category: 'Aggregate Functions', name: 'MAX' },
        { category: 'Aggregate Functions', name: 'MIN' },
        { category: 'Aggregate Functions', name: 'SUM' }
    ];

    const [selectedFunction, setSelectedFunction] = useState(null);
    const sqlExpressionRef = useRef(null);

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const position = { of: '#accordion' };

    const handleFieldChange = useCallback((name, value) => {
        setformFields(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const handleFieldClick = (fieldName) => {
        // Immediately add field to SQL expression
        const currentExpression = formFields.SQLEXPRESSION || '';
        const newExpression = currentExpression + (currentExpression ? ' ' : '') + fieldName;
        handleFieldChange('SQLEXPRESSION', newExpression);
    };

    const handleAddFunction = () => {
        if (selectedFunction) {
            const functionName = selectedFunction.name || selectedFunction;
            const currentExpression = formFields.SQLEXPRESSION || '';
            const newExpression = currentExpression + (currentExpression ? ' ' : '') + functionName + '()';
            handleFieldChange('SQLEXPRESSION', newExpression);
        }
    };

    const handleFunctionClick = (functionItem) => {
        // Set selected function for visual feedback
        setSelectedFunction(functionItem);
        // Immediately add function to SQL expression
        const functionName = functionItem.name || functionItem;
        const currentExpression = formFields.SQLEXPRESSION || '';
        const newExpression = currentExpression + (currentExpression ? ' ' : '') + functionName + '()';
        handleFieldChange('SQLEXPRESSION', newExpression);
    };

    const handleAddWildcard = () => {
        const currentExpression = formFields.SQLEXPRESSION || '';
        const newExpression = currentExpression + (currentExpression ? ' ' : '') + '%';
        handleFieldChange('SQLEXPRESSION', newExpression);
    };

    const loadEvaluationExpressionById = useCallback(async (rowId) => {
        if (!rowId || rowId === '00000000-0000-0000-0000-000000000000') {
            return;
        }

        try {
            setLoadPanelVisible(true);
            const url = `${window._env_.REACT_APP_APIS}/${data.EvaluationExpression.GetEvaluationExpression}?rowid=${rowId}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (response.data) {
                const expr = response.data;
                setformFields({
                    ROWID: expr.RowId || initialFormFields.ROWID,
                    RECORD: expr.RecordNumber || '',
                    EXPRESSIONCODE: expr.ExpressionCode || '',
                    DESCRIPTION: expr.Description || '',
                    STATUS: expr.Status || 'A',
                    COMMENT: expr.Comment || '',
                    SQLEXPRESSION: expr.SqlExpression || ''
                });
            }
        } catch (error) {
            console.error('Error loading Evaluation Expression:', error);
            notify('Failed to load Evaluation Expression data.', 'error', 3000);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [accessToken]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== '00000000-0000-0000-0000-000000000000') {
            loadEvaluationExpressionById(rowId);
        }
    }, [props.id, loadEvaluationExpressionById]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        
        // Validation: Check required fields
        if (!formFields.EXPRESSIONCODE || formFields.EXPRESSIONCODE.trim() === '') {
            notify("Expr Code is required.", "error", 3000);
            setIsFormSubmitted(false);
            return;
        }

        if (!formFields.SQLEXPRESSION || formFields.SQLEXPRESSION.trim() === '') {
            notify("SQL Expression is required.", "error", 3000);
            setIsFormSubmitted(false);
            return;
        }

        setIsFormSubmitted(true);
        setLoadPanelVisible(true);

        try {
            const payload = {
                RowId: formFields.ROWID && formFields.ROWID !== "00000000-0000-0000-0000-000000000000" ? formFields.ROWID : "00000000-0000-0000-0000-000000000000",
                ExpressionCode: formFields.EXPRESSIONCODE || "",
                Description: formFields.DESCRIPTION || "",
                Status: formFields.STATUS || "A",
                SqlExpression: formFields.SQLEXPRESSION || "",
                Comment: formFields.COMMENT || ""
            };

            // Determine if this is an insert or update based on ROWID
            const isUpdate = formFields.ROWID && formFields.ROWID !== "00000000-0000-0000-0000-000000000000";
            const endpoint = isUpdate ? data.EvaluationExpression.UpdateEvaluationExpression : data.EvaluationExpression.InsertEvaluationExpression;
            const method = isUpdate ? 'put' : 'post';
            
            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;
            const resp = await axios[method](url, payload, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });

            const saved = resp?.data;
            if (saved) {
                setformFields((prev) => ({
                    ...prev,
                    ROWID: saved.RowId || prev.ROWID,
                    RECORD: saved.RecordNumber || prev.RECORD,
                }));
            }

            notify("Evaluation Expression saved successfully.", "success", 3000);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                (typeof err?.response?.data === "string" ? err.response.data : null) ||
                err?.message ||
                "Failed to save Evaluation Expression.";

            notify(message, "error", 5000);
        } finally {
            hideLoadPanel();
            setIsFormSubmitted(false);
        }
    };

    const addNewRecordClick = () => {
        setformFields(initialFormFields);
    };

    const handleReset = () => {
        const rowId = props.id;
        if (rowId && rowId !== '00000000-0000-0000-0000-000000000000') {
            loadEvaluationExpressionById(rowId);
        } else {
            setformFields(initialFormFields);
        }
    };

    const goBack = () => {
        history.push("/wm/codes/manage-evaluation-expression");
    };

    // Group functions by category for display
    const groupedFunctions = availableFunctions.reduce((acc, func) => {
        if (!acc[func.category]) {
            acc[func.category] = [];
        }
        acc[func.category].push(func);
        return acc;
    }, {});

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6
                                            style={{
                                                color: window._env_.Color2,
                                                marginLeft: '12px'
                                            }}
                                        >
                                            Manage Evaluation Expression
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        disabled={isFormSubmitted}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>

                            {/* Record Details Section */}
                            <div style={{ marginTop: '20px', marginBottom: '20px', padding: '0 15px' }}>
                                <PrimaryTab
                                    formFields={formFields}
                                    setformFields={setformFields}
                                    formRefs={formRefs}
                                    handleFieldChange={handleFieldChange}
                                    oidc={props.oidc}
                                />
                            </div>

                            {/* Three Column Layout */}
                            <div style={{ display: 'flex', gap: '10px', padding: '0 15px 15px 15px', minHeight: '500px' }}>
                                {/* Left Column - Available Fields */}
                                <ExpressionBaseQueryAvailableFields
                                    expressionType="evaluation"
                                    onFieldClick={handleFieldClick}
                                    height="59vh"
                                />

                                {/* Middle Column - Expression Range */}
                                <div style={{ flex: '2', display: 'flex', flexDirection: 'column', border: '1px solid #ddd', padding: '10px' }}>
                                    <label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Expression Range <span style={{ color: 'red' }}>*</span></label>
                                    <TextArea
                                        ref={sqlExpressionRef}
                                        value={formFields.SQLEXPRESSION}
                                        onValueChanged={(e) => handleFieldChange('SQLEXPRESSION', e.value)}
                                        height="100%"
                                        stylingMode="filled"
                                    />
                                </div>

                                {/* Right Column - Available Functions */}
                                <div style={{ flex: '1', display: 'flex', flexDirection: 'column', border: '1px solid #ddd', padding: '10px' }}>
                                    <label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Available Functions</label>
                                    <div style={{ height: '59vh', overflow: 'auto' }}>
                                        {Object.keys(groupedFunctions).map(category => (
                                            <div key={category} style={{ marginBottom: '15px' }}>
                                                <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#666' }}>{category}</div>
                                                <List
                                                    dataSource={groupedFunctions[category]}
                                                    displayExpr="name"
                                                    height={200}
                                                    selectionMode="single"
                                                    searchEnabled={true}
                                                    onItemClick={(e) => handleFunctionClick(e.itemData)}
                                                    selectedItems={selectedFunction && selectedFunction.category === category ? [selectedFunction] : []}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(Form);
